/**
 * RipRoutingSection — per-org RIP routing map editor + gap panel (Phase 64 /
 * BLU-590 Track A PR2).
 *
 * Edits the `org_rip_routing_maps` doc the bootleg-bridge fetches: per POD
 * product type, colour code → RIP profile folder, colour code → print-file NAME
 * code, and size code → platen. A configured org's map is the SOLE authority
 * for the folder (fail-closed: an unmapped in-use combo is skipped + alerted),
 * so the "Needs attention" panel is the safety net — it scans the catalogue for
 * every in-use combo and surfaces exactly the ones the saved map does not
 * resolve, one click from being mapped. The file-name code fails OPEN (blank =
 * keep the built-in code), which is why its guidance sits beside the Colours
 * table rather than in the section framing: it is a per-row decision, not a
 * whole-section one.
 *
 * RBAC (mirrors ColourCodesSection): the read (`getRipRoutingMap` /
 * `getRipRoutingCoverage`) is OrgSettingsRead, so coordinators SEE this
 * read-only; every write affordance is gated on `useRole().isAdmin()` because
 * `updateRipRoutingMap` / `prefillRipRoutingFromCatalogue` are adminProcedure.
 *
 * Local-edit model: hydrate a local RipRoutingMap from the query, edit locally,
 * Save persists via `updateRipRoutingMap`. "Detect from catalogue" (coverage
 * refetch) and "Prefill defaults" (server-side merge + refetch) are NOT part of
 * the dirty buffer — after prefill the hydration effect re-syncs local from the
 * refetched server map.
 *
 * ORG CODE TABLES: every product-type and size code on this screen is the
 * ORG'S OWN vocabulary, so the org's own names for those codes are what it
 * shows, and its own list is what it offers. `productSync.getCodeTablesForOrg`
 * supplies both; `PRODUCT_TYPE_LABELS` is only the FALLBACK when the org has
 * not named a code, and the bare code is the last resort. The add-a-code
 * inputs are `PrintSizeCodeCombobox` pick-lists (same primitive Print Size
 * Rules uses) that degrade to free text whenever that query is loading, has
 * failed, or the org has no table yet — the section is never gated on it.
 * See `architectural-decisions.md` § "Shared 3PL↔client data is surfaced,
 * never re-typed".
 *
 * OWN-ORG ONLY: unlike `PrintSizeRulesSection` there is no client selector
 * here — `getRipRoutingMap` / `updateRipRoutingMap` are `ctx.organizationId`
 * scoped with no `organizationId` input, so the code-tables query is called
 * with no target org (defaults to the caller's own). If this section ever
 * gains an owner-picks-a-client selector, pass that SAME target org to
 * `getCodeTablesForOrg`, exactly as Print Size Rules does.
 *
 * Mirrors DesignsSourceSection's polish (height-reserved hint↔error, aria-invalid,
 * brand-purple Save) and ColourCodesSection's raw-table + admin-gate shape.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Info,
  Loader2,
  Palette,
  Plus,
  Route,
  Ruler,
  Save,
  ScanSearch,
  Trash2,
  Wand2,
} from 'lucide-react';
import {
  defaultPrintFileCodeFor,
  PLATEN_CODES,
  PRODUCT_TYPE_LABELS,
  RIP_CODE_REGEX,
  type RipRoutingMap,
  type RipRoutingTypeEntry,
} from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PrintSizeCodeCombobox,
  type PrintSizeCodeComboboxStatus,
  type PrintSizeCodeOption,
} from './PrintSizeCodeCombobox';
import { joinStoreNames, buildProductTypeCodeOptions, codeTablesStatus } from './org-code-labels';
import { cleanMapForSave, isFileCodeValid, isFolderValid, isPlatenValid } from './rip-routing-save';

// ---------------------------------------------------------------------------
// Pure helpers (module-level so identity is stable).
// ---------------------------------------------------------------------------

/** Max gap chips rendered per product type before collapsing to "and N more". */
const GAP_CHIP_CAP = 12;
const colourInputId = (pt: string, code: string): string => `rip-colour-${pt}-${code}`;
const fileCodeInputId = (pt: string, code: string): string => `rip-filecode-${pt}-${code}`;
const sizeTriggerId = (pt: string, code: string): string => `rip-size-${pt}-${code}`;

const sortByCode = (a: [string, string], b: [string, string]): number => a[0].localeCompare(b[0]);

/** Product-type key basis: trim, uppercase, strip the dual-print `2` prefix
 *  (matches the backend's `normalizeProductType` in rip-routing.coverage.ts). */
const normalizeTypeCode = (code: string): string => code.trim().toUpperCase().replace(/^2/, '');

/** Store names shown per code before the tail collapses to "+n". */

/**
 * Product-type code → the friendly name operators actually say out loud
 * ("TB · Canvas Tote"). Display only. Three rungs, in order:
 *
 *   1. THE ORG'S OWN name(s) for the code, from its product-sync code table —
 *      an org that calls TB a "Canvas Tote" must never be shown our word,
 *   2. `PRODUCT_TYPE_LABELS`, the built-in fallback,
 *   3. `undefined` → the caller renders the bare code.
 *
 * Never invents a label: a hand-added or new-grammar type reaches rung 3.
 */
const productTypeLabel = (
  pt: string,
  storeNamesByCode?: Map<string, string[]>,
): string | undefined => {
  const key = normalizeTypeCode(pt);
  const own = storeNamesByCode?.get(key);
  if (own && own.length > 0) return joinStoreNames(own);
  return PRODUCT_TYPE_LABELS[key];
};

/**
 * The built-in file-name code that applies when a row's code is blank.
 *
 * `defaultPrintFileCodeFor` is pure + total, so this is safe to call for every
 * row on every render. It deliberately does NOT normalise its input (the four
 * backend call sites disagree about stripping), so we normalise HERE the same
 * way the map keys are built: `computePrefilledRipRoutingMap` seeds file codes
 * from `normalizeProductType(code)` (trim + uppercase + dual `2` stripped) —
 * the same basis `getTypeEntry` resolves against — and an uppercase colour code.
 */
const builtInFileCode = (pt: string, colourCode: string): string =>
  defaultPrintFileCodeFor(
    pt.trim().toUpperCase().replace(/^2/, ''),
    colourCode.trim().toUpperCase(),
  );

// ---------------------------------------------------------------------------
// Section.
// ---------------------------------------------------------------------------

export function RipRoutingSection(): JSX.Element {
  const { t } = useTranslation('settings');
  const { isAdmin } = useRole();
  const admin = isAdmin();

  const mapQuery = trpc.ripRouting.getRipRoutingMap.useQuery();
  // The coverage scan is heavy server-side (BLU-641: three paged scans per
  // scanned org, junction clients included) — never re-fire it on window
  // focus; the explicit refetch() calls below cover every mutation path.
  const coverageQuery = trpc.ripRouting.getRipRoutingCoverage.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // The org's OWN product-type / size vocabulary — the names it sees and the
  // pick-lists it chooses from. No `organizationId`: this section is own-org
  // only (see the file header), so the router's `ctx.organizationId` default
  // is the right target. The section NEVER gates on this query — every
  // consumer degrades to free text.
  const codeTablesQuery = trpc.productSync.getCodeTablesForOrg.useQuery({});
  const utils = trpc.useUtils();

  const [localMap, setLocalMap] = useState<RipRoutingMap>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  // Hydrate local state from the server (initial load + after save/prefill
  // invalidation). React Query's structural sharing keeps the same reference
  // when the doc is deeply equal, so this only re-fires on a real change.
  useEffect(() => {
    if (mapQuery.data) {
      setLocalMap(mapQuery.data.map ?? {});
      setHasChanges(false);
    }
  }, [mapQuery.data]);

  // Focus (and scroll to) a freshly-seeded input after "Map it" opens its
  // accordion. Deferred one frame so the AccordionContent has mounted.
  useEffect(() => {
    if (!pendingFocusId) return undefined;
    const id = pendingFocusId;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.focus();
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
      }
      setPendingFocusId(null);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingFocusId, openItems, localMap]);

  const updateMutation = trpc.ripRouting.updateRipRoutingMap.useMutation({
    onSuccess: () => {
      toast.success('RIP routing saved');
      setHasChanges(false);
      utils.ripRouting.getRipRoutingMap.invalidate();
      utils.ripRouting.getRipRoutingCoverage.invalidate();
    },
    onError: (e) => toast.error(`Could not save: ${e.message}`),
  });

  const prefillMutation = trpc.ripRouting.prefillRipRoutingFromCatalogue.useMutation({
    onSuccess: (res) => {
      // Three counts, because "42 mappings" tells an operator nothing about
      // WHAT changed. Read defensively (annotation, not cast): a server that
      // ever drops the breakdown must still produce an honest toast, never
      // "Prefilled NaN".
      const added: Partial<Record<'colours' | 'sizes' | 'fileCodes', number>> | undefined =
        res.added;
      const { colours, sizes, fileCodes } = added ?? {};
      if (
        typeof colours === 'number' &&
        typeof sizes === 'number' &&
        typeof fileCodes === 'number'
      ) {
        toast.success(t('production.ripRouting.prefilled', { colours, sizes, fileCodes }));
      } else {
        const total = (colours ?? 0) + (sizes ?? 0) + (fileCodes ?? 0);
        toast.success(t('production.ripRouting.prefilledTotal', { count: total }));
      }
      // Re-sync local from the refetched server map (prefill wrote server-side).
      utils.ripRouting.getRipRoutingMap.invalidate();
      utils.ripRouting.getRipRoutingCoverage.invalidate();
    },
    onError: (e) => toast.error(`Could not prefill: ${e.message}`),
  });

  const coverage = coverageQuery.data;

  // Union of saved-map keys + in-use catalogue product types, sorted.
  const productTypeCodes = useMemo(() => {
    const set = new Set<string>(Object.keys(localMap));
    for (const pt of coverage?.productTypes ?? []) set.add(pt);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [localMap, coverage]);

  // Gaps grouped by product type (colour + size codes the saved map misses).
  const gapGroups = useMemo(() => {
    if (!coverage) return [] as Array<{ pt: string; colours: string[]; sizes: string[] }>;
    const m = new Map<string, { colours: string[]; sizes: string[] }>();
    const ensure = (pt: string) => {
      let g = m.get(pt);
      if (!g) {
        g = { colours: [], sizes: [] };
        m.set(pt, g);
      }
      return g;
    };
    for (const g of coverage.gaps.colourGaps) ensure(g.productType).colours.push(g.code);
    for (const g of coverage.gaps.sizeGaps) ensure(g.productType).sizes.push(g.code);
    return [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([pt, g]) => ({ pt, colours: g.colours, sizes: g.sizes }));
  }, [coverage]);

  const typesWithGaps = useMemo(() => new Set(gapGroups.map((g) => g.pt)), [gapGroups]);
  const totalGaps =
    (coverage?.gaps.colourGaps.length ?? 0) + (coverage?.gaps.sizeGaps.length ?? 0);

  // ---- the org's own code tables: names first, then pick-lists ----

  const { typeOptions, typeStoreNamesByCode } = useMemo(() => buildProductTypeCodeOptions(
    codeTablesQuery.data?.productTypes,
    productTypeCodes,
    {
      custom: t('production.printSizeRules.badgeCustom'),
      singleSize: t('production.printSizeRules.badgeSingleSize'),
      alreadyAdded: t('production.printSizeRules.alreadyAdded'),
      invalidCode: t('production.codeOptions.invalidCode'),
    }, normalizeTypeCode,
  ), [codeTablesQuery.data, productTypeCodes, t]);
  const typeComboboxStatus = codeTablesStatus(codeTablesQuery, typeOptions.length);

  // Org-wide size vocabulary. Per-type `disabled` is layered on inside
  // RipProductTypeItem from that type's OWN already-mapped sizes.
  const sizeOptionsBase: PrintSizeCodeOption[] = useMemo(
    () =>
      (codeTablesQuery.data?.sizes ?? [])
        .map((row) => ({ code: row.code, label: joinStoreNames(row.storeNames) }))
        .sort((a, b) => a.code.localeCompare(b.code)),
    [codeTablesQuery.data],
  );

  const sizeStoreNamesByCode = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const row of codeTablesQuery.data?.sizes ?? []) m.set(row.code, row.storeNames);
    return m;
  }, [codeTablesQuery.data]);

  const sizeComboboxStatus = codeTablesStatus(codeTablesQuery, sizeOptionsBase.length);

  // No product-sync row at all: the sizes offered are the built-in defaults
  // and there are no product types to offer. Say so once, per list.
  const noCodeTable = codeTablesQuery.data != null && !codeTablesQuery.data.exists;

  // Any leaf that would fail the write-path zod — blocks Save (colour AND text,
  // so it never relies on colour alone, per DESIGN §16).
  const anyInvalid = useMemo(() => {
    for (const entry of Object.values(localMap)) {
      for (const folder of Object.values(entry.colours ?? {})) {
        if (!isFolderValid(folder)) return true;
      }
      for (const platen of Object.values(entry.sizes ?? {})) {
        if (!isPlatenValid(platen)) return true;
      }
      // Blank = "built-in rule" and is valid; only a non-empty malformed code
      // blocks Save.
      for (const value of Object.values(entry.fileCodes ?? {})) {
        if (value.trim().length > 0 && !isFileCodeValid(value)) return true;
      }
    }
    return false;
  }, [localMap]);

  // ---- local-edit mutators (read the latest localMap via closure) ----

  const setColourFolder = (pt: string, code: string, folder: string): void => {
    setLocalMap((prev) => {
      const entry = prev[pt] ?? {};
      return { ...prev, [pt]: { ...entry, colours: { ...(entry.colours ?? {}), [code]: folder } } };
    });
    setHasChanges(true);
  };

  const setColourFileCode = (pt: string, code: string, value: string): void => {
    setLocalMap((prev) => {
      const entry = prev[pt] ?? {};
      return {
        ...prev,
        [pt]: { ...entry, fileCodes: { ...(entry.fileCodes ?? {}), [code]: value } },
      };
    });
    setHasChanges(true);
  };

  const removeColour = (pt: string, code: string): void => {
    setLocalMap((prev) => {
      const entry = prev[pt];
      if (!entry) return prev;
      // Remove BOTH leaves for this colour — a leftover file code with no row
      // to render it would be invisible but still persisted.
      const colours = { ...(entry.colours ?? {}) };
      delete colours[code];
      const fileCodes = { ...(entry.fileCodes ?? {}) };
      delete fileCodes[code];
      return { ...prev, [pt]: { ...entry, colours, fileCodes } };
    });
    setHasChanges(true);
  };

  const addColour = (pt: string, code: string, folder: string): boolean => {
    const c = code.trim().toUpperCase();
    const f = folder.trim();
    if (!RIP_CODE_REGEX.test(c)) {
      toast.error(t('production.ripRouting.colourCodeInvalid'));
      return false;
    }
    if (!isFolderValid(f)) {
      toast.error(t('production.ripRouting.folderInvalid'));
      return false;
    }
    const existingColours = localMap[pt]?.colours;
    if (existingColours && Object.prototype.hasOwnProperty.call(existingColours, c)) {
      toast.error(t('production.ripRouting.duplicateCode', { code: c, pt }));
      return false;
    }
    setColourFolder(pt, c, f);
    return true;
  };

  const setSizePlaten = (pt: string, code: string, platen: string): void => {
    setLocalMap((prev) => {
      const entry = prev[pt] ?? {};
      return { ...prev, [pt]: { ...entry, sizes: { ...(entry.sizes ?? {}), [code]: platen } } };
    });
    setHasChanges(true);
  };

  const removeSize = (pt: string, code: string): void => {
    setLocalMap((prev) => {
      const entry = prev[pt];
      if (!entry?.sizes) return prev;
      const sizes = { ...entry.sizes };
      delete sizes[code];
      return { ...prev, [pt]: { ...entry, sizes } };
    });
    setHasChanges(true);
  };

  /**
   * Add a size row from the org's own size vocabulary (or a code typed into
   * the same combobox). The row is seeded with NO platen and focus jumps to
   * that row's platen select — exactly what "Map it" does for a size gap, so
   * there is ONE way a size row appears, not two. An empty platen shows no
   * red error (the "Choose platen" placeholder guides) but does block Save
   * via `anyInvalid`.
   */
  const handleSelectSizeCode = (pt: string, rawCode: string): boolean => {
    const c = rawCode.trim().toUpperCase();
    if (!RIP_CODE_REGEX.test(c)) {
      toast.error(t('production.ripRouting.sizeCodeInvalid'));
      return false;
    }
    const existingSizes = localMap[pt]?.sizes;
    if (existingSizes && Object.prototype.hasOwnProperty.call(existingSizes, c)) {
      toast.error(t('production.ripRouting.duplicateCode', { code: c, pt }));
      return false;
    }
    setSizePlaten(pt, c, '');
    setPendingFocusId(sizeTriggerId(pt, c));
    return true;
  };

  // "Map it": ensure the type's accordion is open, seed a blank leaf if absent,
  // then focus it. A present-but-invalid leaf is left untouched (just focused).
  const mapGap = (pt: string, code: string, kind: 'colour' | 'size'): void => {
    setOpenItems((prev) => (prev.includes(pt) ? prev : [...prev, pt]));
    if (kind === 'colour') {
      setLocalMap((prev) => {
        const entry = prev[pt] ?? {};
        if (entry.colours && Object.prototype.hasOwnProperty.call(entry.colours, code)) return prev;
        return { ...prev, [pt]: { ...entry, colours: { ...(entry.colours ?? {}), [code]: '' } } };
      });
      setHasChanges(true);
      setPendingFocusId(colourInputId(pt, code));
    } else {
      setLocalMap((prev) => {
        const entry = prev[pt] ?? {};
        if (entry.sizes && Object.prototype.hasOwnProperty.call(entry.sizes, code)) return prev;
        return { ...prev, [pt]: { ...entry, sizes: { ...(entry.sizes ?? {}), [code]: '' } } };
      });
      setHasChanges(true);
      setPendingFocusId(sizeTriggerId(pt, code));
    }
  };

  // Add a product type the catalogue scan didn't surface (non-standard
  // grammar, or a type not yet in use) — picked from the org's own product
  // types, or typed. Seeds an empty entry + opens its accordion.
  const handleSelectProductType = (rawCode: string): boolean => {
    const code = rawCode.trim().toUpperCase();
    if (!RIP_CODE_REGEX.test(code)) {
      toast.error(t('production.ripRouting.productTypeInvalid'));
      return false;
    }
    if (productTypeCodes.includes(code)) {
      toast.error(t('production.ripRouting.duplicateProductType', { code }));
      return false;
    }
    setLocalMap((prev) => ({ ...prev, [code]: {} }));
    setOpenItems((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setHasChanges(true);
    return true;
  };

  const handleDetect = (): void => {
    void coverageQuery.refetch();
  };

  const handleSave = (): void => {
    updateMutation.mutate({ map: cleanMapForSave(localMap) });
  };

  // ---- states ----

  // Page-level query error.
  if (mapQuery.isError || coverageQuery.isError) {
    return (
      <div className="space-y-3">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            Could not load RIP routing. {mapQuery.error?.message ?? coverageQuery.error?.message ?? ''}
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void mapQuery.refetch();
            void coverageQuery.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Initial load skeleton (shimmer, row-height matched).
  if (mapQuery.isLoading || coverageQuery.isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const stats = coverage?.stats;

  return (
    <div className="space-y-4">
      {/* 1. Framing — ONE muted line so the amber panel stays the sole anchor.
          What the map is FOR now lives in the section/step description above;
          this line carries the one thing that isn't obvious (a saved map
          replaces the bridge's built-in routing, and an unmapped colour is
          skipped). The file-name-code guidance used to stack a second Info
          paragraph here — it now sits beside the table it explains. */}
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="leading-relaxed">{t('production.ripRouting.intro')}</span>
      </p>

      {/* 2. "Needs attention" panel — the centrepiece. */}
      {totalGaps > 0 && (
        <section
          className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
        >
          <div
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
            aria-live="polite"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
              <span
                className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-amber-800 ring-1 ring-amber-300/80"
              >
                {totalGaps}
              </span>
              <span className="text-sm font-medium">
                {totalGaps === 1 ? 'mapping needs attention' : 'mappings need attention'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 border-amber-300 bg-transparent text-xs text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                onClick={handleDetect}
                disabled={coverageQuery.isFetching}
              >
                {coverageQuery.isFetching ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <ScanSearch className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {t('production.ripRouting.detect')}
              </Button>
              {admin && (
                <>
                  {hasChanges && (
                    <span className="text-xs text-amber-700">
                      {t('production.ripRouting.prefillBlockedDirty')}
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 border-amber-300 bg-transparent text-xs text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                    onClick={() => prefillMutation.mutate()}
                    disabled={prefillMutation.isPending || hasChanges}
                  >
                    {prefillMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    {t('production.ripRouting.prefill')}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Gaps grouped by product type (heading carries the type — code AND
              friendly name, so an operator who thinks in "tote bag" can find
              it; chips carry the code). Capped per type so a large catalogue
              can't wall the panel; the header pill still shows the honest full
              count. */}
          <div className="space-y-2">
            {gapGroups.map((g) => {
              const combined = g.colours.length + g.sizes.length;
              const colourShown = g.colours.slice(0, GAP_CHIP_CAP);
              const sizeShown = g.sizes.slice(0, Math.max(0, GAP_CHIP_CAP - colourShown.length));
              const overflow = combined - colourShown.length - sizeShown.length;
              const ptLabel = productTypeLabel(g.pt, typeStoreNamesByCode);
              return (
                <div key={g.pt} className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-xs font-semibold text-amber-900">{g.pt}</span>
                  {ptLabel && <span className="text-xs text-amber-800">· {ptLabel}</span>}
                  {colourShown.map((code) => (
                    <GapChip
                      key={`c-${code}`}
                      pt={g.pt}
                      code={code}
                      kind="colour"
                      admin={admin}
                      onMap={mapGap}
                      mapItLabel={t('production.ripRouting.mapIt')}
                    />
                  ))}
                  {sizeShown.map((code) => (
                    <GapChip
                      key={`s-${code}`}
                      pt={g.pt}
                      code={code}
                      kind="size"
                      admin={admin}
                      onMap={mapGap}
                      mapItLabel={t('production.ripRouting.mapIt')}
                    />
                  ))}
                  {overflow > 0 && (
                    <span className="text-xs text-amber-800">and {overflow} more</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Honest limits — the scan is NOT exhaustive. */}
          <div className="space-y-0.5 text-xs">
            {!admin && <p className="text-amber-800">Ask an admin to map these.</p>}
            {stats && stats.unparseable > 0 && (
              <p className="text-amber-800">
                {stats.unparseable === 1 ? '1 catalogue SKU' : `${stats.unparseable} catalogue SKUs`}{' '}
                couldn&apos;t be read automatically (unknown format or product type). Map those by
                hand, or add the product type below so the next scan recognises it.
              </p>
            )}
            {stats && stats.custUnparseable > 0 && (
              <p className="text-amber-800">
                {stats.custUnparseable === 1
                  ? '1 custom print file'
                  : `${stats.custUnparseable} custom print files`}{' '}
                couldn&apos;t be read automatically.
              </p>
            )}
            <p className="text-amber-800">
              The scan covers your POD catalogue and known custom print files. It can still miss
              combinations, so treat it as a starting point, not a guarantee.
            </p>
            {(stats?.capped || stats?.custCapped) && (
              <p className="text-amber-800">
                The scan reached its limit, so some combinations may be missing.
              </p>
            )}
          </div>
        </section>
      )}

      {/* 3. Empty state vs product-type accordion. */}
      {productTypeCodes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-10 text-center">
          <div className="rounded-full bg-muted p-3">
            <Route className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">No routing configured yet</p>
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
              Scan your catalogue to find the colours and sizes your POD products use, then map each
              one.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDetect} disabled={coverageQuery.isFetching}>
              {coverageQuery.isFetching ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ScanSearch className="mr-1.5 h-4 w-4" aria-hidden="true" />
              )}
              {t('production.ripRouting.detect')}
            </Button>
            {admin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => prefillMutation.mutate()}
                disabled={prefillMutation.isPending || hasChanges}
              >
                {prefillMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Wand2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                )}
                {t('production.ripRouting.prefill')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Routing by product type
          </p>
          {/* Add a product type absent from the scan (non-standard grammar) —
              the org's OWN product types as a pick-list, still creatable by
              typing, and degrading to plain free text while the code-tables
              query loads or fails (PrintSizeCodeCombobox owns both). */}
          {admin && (
            <div className="space-y-1.5">
              <PrintSizeCodeCombobox
                emptyLabel={t('production.codeOptions.noMatches')}
                options={typeOptions}
                status={typeComboboxStatus}
                onSelect={handleSelectProductType}
                placeholder={t('production.printSizeRules.typeComboboxPlaceholder')}
                ariaLabel={t('production.printSizeRules.typeComboboxAriaLabel')}
                addButtonLabel={t('production.ripRouting.addProductType')}
                createLabel={(code) => t('production.printSizeRules.createCode', { code })}
                errorHint={t('production.ripRouting.loadErrorHint')}
                className="sm:w-72"
              />
              {typeComboboxStatus === 'empty' && (
                <p className="text-xs text-muted-foreground">
                  {t('production.ripRouting.typeCodesEmptyHint')}
                </p>
              )}
            </div>
          )}
          <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="space-y-2">
            {productTypeCodes.map((pt) => (
              <RipProductTypeItem
                key={pt}
                productType={pt}
                label={productTypeLabel(pt, typeStoreNamesByCode)}
                entry={localMap[pt]}
                admin={admin}
                hasGap={typesWithGaps.has(pt)}
                onSetColour={setColourFolder}
                onSetFileCode={setColourFileCode}
                onRemoveColour={removeColour}
                onAddColour={addColour}
                onSetSize={setSizePlaten}
                onRemoveSize={removeSize}
                onSelectSize={handleSelectSizeCode}
                sizeOptions={sizeOptionsBase}
                sizeStatus={sizeComboboxStatus}
                sizeStoreNamesByCode={sizeStoreNamesByCode}
                showSizeDefaultsHint={noCodeTable}
              />
            ))}
          </Accordion>
        </div>
      )}

      {/* 4. Save (admin + dirty only). */}
      {admin && hasChanges && (
        <div className="flex items-center justify-end gap-3">
          {anyInvalid && (
            <span className="text-xs text-amber-700" role="status">
              Finish or fix the highlighted mappings to save.
            </span>
          )}
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending || anyInvalid}
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            {t('production.ripRouting.save')}
          </Button>
        </div>
      )}

      {/* Read-only hint for non-admins (mirror ColourCodesSection). */}
      {!admin && (
        <p className="text-sm text-muted-foreground/70">Only admins can edit routing.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gap chip — a single "Map it" affordance for one (productType, code) gap.
// Colour vs size is signalled by icon (never colour alone).
// ---------------------------------------------------------------------------

interface GapChipProps {
  pt: string;
  code: string;
  kind: 'colour' | 'size';
  admin: boolean;
  mapItLabel: string;
  onMap: (pt: string, code: string, kind: 'colour' | 'size') => void;
}

function GapChip({ pt, code, kind, admin, mapItLabel, onMap }: GapChipProps): JSX.Element {
  const Icon = kind === 'colour' ? Palette : Ruler;
  const kindWord = kind === 'colour' ? 'colour' : 'size';
  if (!admin) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs text-amber-800 ring-1 ring-amber-300/80">
        <Icon className="h-3 w-3" aria-hidden="true" />
        <span className="font-mono">{code}</span>
        <span className="sr-only">{`${kindWord} for ${pt}`}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onMap(pt, code, kind)}
      aria-label={`${mapItLabel}: ${kindWord} ${code} for ${pt}`}
      className="inline-flex min-h-[24px] items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-amber-300/80 transition-colors duration-200 hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 motion-reduce:transition-none"
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span className="font-mono">{code}</span>
      <span>{mapItLabel}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// One product-type accordion item: colour→(profile folder + file name code)
// and size→platen mini-tables.
// ---------------------------------------------------------------------------

interface RipProductTypeItemProps {
  productType: string;
  /** Resolved by `productTypeLabel` upstream: the org's own name(s) for this
   *  code, else the built-in fallback, else undefined (render the code only). */
  label: string | undefined;
  entry: RipRoutingTypeEntry | undefined;
  admin: boolean;
  hasGap: boolean;
  onSetColour: (pt: string, code: string, folder: string) => void;
  onSetFileCode: (pt: string, code: string, value: string) => void;
  onRemoveColour: (pt: string, code: string) => void;
  onAddColour: (pt: string, code: string, folder: string) => boolean;
  onSetSize: (pt: string, code: string, platen: string) => void;
  onRemoveSize: (pt: string, code: string) => void;
  onSelectSize: (pt: string, code: string) => boolean;
  /** Org-wide size-code vocabulary from the org's product-sync table. */
  sizeOptions: PrintSizeCodeOption[];
  sizeStatus: PrintSizeCodeComboboxStatus;
  sizeStoreNamesByCode: Map<string, string[]>;
  showSizeDefaultsHint: boolean;
}

function RipProductTypeItem({
  productType: pt,
  label: ptLabel,
  entry,
  admin,
  hasGap,
  onSetColour,
  onSetFileCode,
  onRemoveColour,
  onAddColour,
  onSetSize,
  onRemoveSize,
  onSelectSize,
  sizeOptions,
  sizeStatus,
  sizeStoreNamesByCode,
  showSizeDefaultsHint,
}: RipProductTypeItemProps): JSX.Element {
  const { t } = useTranslation('settings');
  const [newColourCode, setNewColourCode] = useState('');
  const [newColourFolder, setNewColourFolder] = useState('');
  const [newColourFileCode, setNewColourFileCode] = useState('');

  // One row per colour carrying BOTH rules, so the row set is the UNION of the
  // two sub-tables. A fileCodes-only row (a file name code with no profile
  // folder) is RENDER-ONLY: `addColour` deliberately requires a folder, so such
  // a row can only arrive from prefill or a direct DB write. It must still be
  // visible and editable, because for a CONFIGURED org a blank folder fails
  // CLOSED — the bridge SKIPS that file (it does not fall back to a built-in
  // folder), so an unrendered row would be an invisible outage.
  const colourEntries: Array<[string, string]> = [
    ...new Set([
      ...Object.keys(entry?.colours ?? {}),
      ...Object.keys(entry?.fileCodes ?? {}),
    ]),
  ]
    .map((code): [string, string] => [code, entry?.colours?.[code] ?? ''])
    .sort(sortByCode);
  const sizeEntries = Object.entries(entry?.sizes ?? {}).sort(sortByCode);

  // Layer THIS type's already-mapped codes onto the org-wide size vocabulary.
  const sizeOptionsForType: PrintSizeCodeOption[] = sizeOptions.map((opt) => {
    const alreadyMapped = Object.prototype.hasOwnProperty.call(entry?.sizes ?? {}, opt.code);
    return alreadyMapped
      ? { ...opt, disabled: true, disabledReason: t('production.printSizeRules.alreadyAdded') }
      : opt;
  });

  const tryAddColour = (): void => {
    const fileCode = newColourFileCode.trim();
    // Blank is allowed (built-in rule); a non-empty malformed code is not.
    if (fileCode.length > 0 && !isFileCodeValid(fileCode)) {
      toast.error(t('production.ripRouting.fileCodeInvalid'));
      return;
    }
    if (onAddColour(pt, newColourCode, newColourFolder)) {
      if (fileCode.length > 0) {
        onSetFileCode(pt, newColourCode.trim().toUpperCase(), fileCode);
      }
      setNewColourCode('');
      setNewColourFolder('');
      setNewColourFileCode('');
    }
  };

  return (
    <AccordionItem value={pt} className="rounded-lg border">
      <AccordionTrigger className="rounded-t-lg px-3 py-3 hover:no-underline hover:bg-muted/30">
        <div className="flex min-w-0 items-center gap-3">
          <Route className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="font-mono text-sm font-semibold">{pt}</span>
          {/* Code first (it is what the SKU carries), friendly name second (it
              is what the operator says). No label = code only, never a guess. */}
          {ptLabel && (
            <span className="truncate text-sm text-muted-foreground">· {ptLabel}</span>
          )}
          <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
            {colourEntries.length} colours · {sizeEntries.length} sizes
          </span>
          {hasGap && (
            <span className="inline-flex items-center">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              <span className="sr-only">has unmapped combinations</span>
            </span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="space-y-5 px-3 pb-4 pt-0">
        {/* Colours → profile folder + file name code */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Palette className="h-3.5 w-3.5" aria-hidden="true" />
            {t('production.ripRouting.coloursHeading')}
          </div>

          {/* File-name-code guidance sits with the column it explains, not in
              the section framing: it is the one column that FALLS BACK, and the
              caution (renaming orphans existing files) only matters here. */}
          <div className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <div className="space-y-1">
              <p>{t('production.ripRouting.fileCodeHint')}</p>
              <p className="text-amber-700">{t('production.ripRouting.fileCodeCaution')}</p>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th scope="col" className="w-28 px-3 py-2 font-medium">
                    {t('production.ripRouting.colourCodeHeader')}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t('production.ripRouting.profileFolderHeader')}
                  </th>
                  <th scope="col" className="w-44 px-3 py-2 font-medium">
                    {t('production.ripRouting.fileCode')}
                  </th>
                  {admin && (
                    <th scope="col" className="w-12">
                      <span className="sr-only">{t('production.ripRouting.actionsHeader')}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {colourEntries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={admin ? 4 : 3}
                      className="px-3 py-3 text-center text-xs text-muted-foreground/70"
                    >
                      {t('production.ripRouting.noColours')}
                    </td>
                  </tr>
                ) : (
                  colourEntries.map(([code, folder]) => {
                    // Red error only for a non-empty, malformed folder. An
                    // untouched-empty seed shows a neutral helper, never an alert
                    // (mirrors DesignsSourceSection's folderInvalid gate, DESIGN §9).
                    const trimmed = folder.trim();
                    const folderInvalid = trimmed.length > 0 && !isFolderValid(folder);
                    const hintId = `${colourInputId(pt, code)}-hint`;
                    const errorId = `${colourInputId(pt, code)}-error`;
                    // File-name code: blank is a valid, meaningful state — it
                    // means "keep the built-in rule for this product type", so
                    // only a non-empty malformed value is an error.
                    const fileCode = entry?.fileCodes?.[code] ?? '';
                    const fileCodeTrimmed = fileCode.trim();
                    const fileCodeInvalid =
                      fileCodeTrimmed.length > 0 && !isFileCodeValid(fileCode);
                    const fileHintId = `${fileCodeInputId(pt, code)}-hint`;
                    const fileErrorId = `${fileCodeInputId(pt, code)}-error`;
                    // Prefilled-to-the-built-in vs genuinely overridden. Blank
                    // and "same as built-in" both mean the station keeps looking
                    // for today's name, so they read the same. Compared
                    // case-SENSITIVELY: the value lands in a filename verbatim.
                    const builtIn = builtInFileCode(pt, code);
                    const usesBuiltIn =
                      fileCodeTrimmed.length === 0 || fileCodeTrimmed === builtIn;
                    return (
                      <tr key={code} className="border-b align-top last:border-b-0">
                        <td className="px-3 py-2 font-mono">{code}</td>
                        <td className="px-3 py-2">
                          {admin ? (
                            <>
                              <Input
                                id={colourInputId(pt, code)}
                                value={folder}
                                placeholder="e.g. Bootleg B"
                                className="h-11 max-w-xs font-mono"
                                aria-invalid={folderInvalid || undefined}
                                aria-describedby={
                                  folderInvalid
                                    ? errorId
                                    : trimmed.length === 0
                                      ? hintId
                                      : undefined
                                }
                                aria-label={t('production.ripRouting.folderAria', { code, pt })}
                                onChange={(e) => onSetColour(pt, code, e.target.value)}
                              />
                              {/* Height reserved so a row never jumps; the hint
                                  and the error are SEPARATE nodes so role="alert"
                                  fires on appearance, not on a text swap. */}
                              <div className="mt-0.5 min-h-[1rem] max-w-xs text-xs">
                                {folderInvalid ? (
                                  <p id={errorId} role="alert" className="text-destructive">
                                    {t('production.ripRouting.folderInvalid')}
                                  </p>
                                ) : trimmed.length === 0 ? (
                                  <p id={hintId} className="text-muted-foreground">
                                    {t('production.ripRouting.folderHint')}
                                  </p>
                                ) : null}
                              </div>
                            </>
                          ) : (
                            <span className="font-mono">
                              {folder || (
                                <span className="text-muted-foreground">
                                  {t('production.ripRouting.notSet')}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {admin ? (
                            <>
                              <Input
                                id={fileCodeInputId(pt, code)}
                                value={fileCode}
                                placeholder={t('production.ripRouting.fileCodeAuto')}
                                maxLength={8}
                                className="h-11 w-40 font-mono"
                                aria-invalid={fileCodeInvalid || undefined}
                                aria-describedby={fileCodeInvalid ? fileErrorId : fileHintId}
                                aria-label={t('production.ripRouting.fileCodeAria', { code, pt })}
                                onChange={(e) => onSetFileCode(pt, code, e.target.value)}
                              />
                              <div className="mt-0.5 min-h-[2rem] w-40 text-xs">
                                {fileCodeInvalid ? (
                                  <p id={fileErrorId} role="alert" className="text-destructive">
                                    {t('production.ripRouting.fileCodeInvalid')}
                                  </p>
                                ) : (
                                  <p id={fileHintId} className="text-muted-foreground">
                                    {usesBuiltIn
                                      ? t('production.ripRouting.fileCodeBuiltIn', {
                                          code: builtIn,
                                        })
                                      : t('production.ripRouting.fileCodeOverride', {
                                          code: builtIn,
                                        })}
                                  </p>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="font-mono">
                              {fileCodeTrimmed || (
                                <span className="text-muted-foreground">
                                  {t('production.ripRouting.fileCodeAuto')}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        {admin && (
                          <td className="px-2 py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-11 w-11 text-muted-foreground hover:text-foreground"
                              aria-label={t('production.ripRouting.removeColourAria', { code, pt })}
                              onClick={() => onRemoveColour(pt, code)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {admin && (
            // Same left-to-right order (code, folder, file name code) as the
            // table above, so the add row reads as one more row rather than a
            // separate form. Widths track their columns, except that the two
            // code inputs are one step wider than their column: their
            // placeholders NAME the field (there is no header above the add
            // row) and clip at the column width.
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                value={newColourCode}
                placeholder={t('production.ripRouting.colourCodePlaceholder')}
                className="h-11 font-mono sm:w-32"
                aria-label={t('production.ripRouting.newColourCodeAria', { pt })}
                onChange={(e) => setNewColourCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    tryAddColour();
                  }
                }}
              />
              <Input
                value={newColourFolder}
                placeholder={t('production.ripRouting.folderPlaceholder')}
                className="h-11 flex-1 font-mono"
                aria-label={t('production.ripRouting.newFolderAria', { pt })}
                onChange={(e) => setNewColourFolder(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    tryAddColour();
                  }
                }}
              />
              <Input
                value={newColourFileCode}
                placeholder={t('production.ripRouting.fileCodePlaceholder')}
                maxLength={8}
                className="h-11 font-mono sm:w-48"
                aria-label={t('production.ripRouting.newFileCodeAria', { pt })}
                onChange={(e) => setNewColourFileCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    tryAddColour();
                  }
                }}
              />
              <Button
                variant="outline"
                className="h-11 gap-1"
                onClick={tryAddColour}
                disabled={!newColourCode.trim() || !newColourFolder.trim()}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {t('production.ripRouting.addColour')}
              </Button>
            </div>
          )}
        </div>

        {/* Sizes → platen */}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Ruler className="h-3.5 w-3.5" aria-hidden="true" />
            {t('production.ripRouting.sizesHeading')}
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th scope="col" className="w-28 px-3 py-2 font-medium">
                    {t('production.ripRouting.sizeCodeHeader')}
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t('production.ripRouting.platenHeader')}
                  </th>
                  {admin && (
                    <th scope="col" className="w-12">
                      <span className="sr-only">{t('production.ripRouting.actionsHeader')}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sizeEntries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={admin ? 3 : 2}
                      className="px-3 py-3 text-center text-xs text-muted-foreground/70"
                    >
                      {t('production.ripRouting.noSizes')}
                    </td>
                  </tr>
                ) : (
                  sizeEntries.map(([code, platen]) => {
                    // Empty seeds show NO red error: the "Choose platen" placeholder
                    // already guides. Only a non-empty stale value flags red
                    // (DESIGN §9). Empty still blocks Save via `anyInvalid`.
                    const platenInvalid = platen.length > 0 && !isPlatenValid(platen);
                    const errorId = `${sizeTriggerId(pt, code)}-error`;
                    // The org's OWN name(s) for this size code, under the code
                    // it actually stores. Absent for a code it never named.
                    const mappedStoreNames = sizeStoreNamesByCode.get(code);
                    return (
                      <tr key={code} className="border-b align-top last:border-b-0">
                        <td className="px-3 py-2">
                          <span className="font-mono">{code}</span>
                          {mappedStoreNames && mappedStoreNames.length > 0 && (
                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                              {joinStoreNames(mappedStoreNames)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {admin ? (
                            <>
                              <Select value={platen} onValueChange={(v) => onSetSize(pt, code, v)}>
                                <SelectTrigger
                                  id={sizeTriggerId(pt, code)}
                                  className="h-11 w-40"
                                  aria-invalid={platenInvalid || undefined}
                                  aria-describedby={platenInvalid ? errorId : undefined}
                                  aria-label={t('production.ripRouting.platenAria', { code, pt })}
                                >
                                  <SelectValue
                                    placeholder={t('production.ripRouting.choosePlaten')}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {PLATEN_CODES.map((p) => (
                                    <SelectItem key={p} value={p}>
                                      {p}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {/* Height reserved so choosing a platen never
                                  reflows the row; the alert is its own node. */}
                              <div className="mt-0.5 min-h-[1rem] w-40 text-xs">
                                {platenInvalid && (
                                  <p id={errorId} role="alert" className="text-destructive">
                                    {t('production.ripRouting.platenRequired')}
                                  </p>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="font-mono">
                              {platen || (
                                <span className="text-muted-foreground">
                                  {t('production.ripRouting.notSet')}
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        {admin && (
                          <td className="px-2 py-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-11 w-11 text-muted-foreground hover:text-foreground"
                              aria-label={t('production.ripRouting.removeSizeAria', { code, pt })}
                              onClick={() => onRemoveSize(pt, code)}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Add a size from the org's OWN size vocabulary (still creatable by
              typing, still plain free text while the query loads or fails).
              Picking seeds the row and focuses its platen select, so the
              platen is chosen in the row it belongs to — the same move "Map
              it" makes for a size gap. */}
          {admin && (
            <div className="space-y-1.5">
              <PrintSizeCodeCombobox
                emptyLabel={t('production.codeOptions.noMatches')}
                options={sizeOptionsForType}
                status={sizeStatus}
                onSelect={(code) => onSelectSize(pt, code)}
                placeholder={t('production.printSizeRules.sizeComboboxPlaceholder')}
                ariaLabel={t('production.printSizeRules.sizeComboboxAriaLabel', {
                  productType: pt,
                })}
                addButtonLabel={t('production.ripRouting.addSize')}
                createLabel={(code) => t('production.printSizeRules.createCode', { code })}
                errorHint={t('production.ripRouting.loadErrorHint')}
              />
              {showSizeDefaultsHint && (
                <p className="text-xs text-muted-foreground">
                  {t('production.ripRouting.sizeDefaultsHint')}
                </p>
              )}
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
